/**
 * The app shell: logo → "/", the one search field, the avatar with its menu. That is all the chrome
 * there is. On Now the field is 'hero' and the header floats over the content; elsewhere it is a slim
 * sticky glass bar. The trail (the chips of a drill) sits under the field when a page passes one.
 */
import { useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import * as Popover from '@radix-ui/react-popover';
import { useAuth } from '../lib/auth.tsx';
import { useThemePref, type ThemePref } from '../lib/theme.tsx';
import { Api, ApiError } from '../lib/api.ts';
import { useEnv } from '../lib/envs.tsx';
import { envColor, showBand } from '../lib/env.ts';
import { useToast } from '../components/ui.tsx';
import { BuildingIcon, CheckIcon, ChevronIcon, GearIcon, KeyIcon, Logo, LogoutIcon, MoonIcon, NetworkIcon, PulseIcon, SunIcon } from '../components/icons.tsx';
import { Menu, Surface, cx } from '../design/primitives.tsx';
import { SearchField } from './SearchField.tsx';
import '../systems/systems.css';

export interface ShellSearch {
  value: string;
  onSubmit: (q: string) => void;
  size: 'hero' | 'compact';
  /** The trail chips, shown under the field. */
  trail?: ReactNode;
}

/**
 * `--shell-h` on the root is the header's height (88 px hero, 56 px compact) for pages that lay out
 * against it; the hero header takes no space, so Now starts its content with `pt-[var(--shell-h)]`.
 */
export function Shell({ search, children, className }: { search?: ShellSearch; children: ReactNode; className?: string }) {
  const hero = search?.size === 'hero';
  const { link } = useEnv();
  return (
    <div className={cx('min-h-screen bg-n1 text-n10 font-sans text-body', className)} style={{ '--shell-h': hero ? '88px' : '56px' } as CSSProperties}>
      <EnvBand />
      <header className={cx('z-30 grid items-start gap-4 px-6',
        hero ? 'absolute inset-x-0 top-0 pt-4 pointer-events-none grid-cols-[minmax(52px,1fr)_minmax(0,720px)_1fr]'
          : 'sticky top-0 min-h-14 py-2 bg-n1/80 backdrop-blur-md grid-cols-[1fr_minmax(0,600px)_1fr]')}>
        <div className="pointer-events-auto flex items-center gap-3 min-w-0">
          <Link to={link('/')} className="inline-flex items-center gap-2.5 h-10 w-fit shrink-0 text-n10 no-underline">
            <Logo />
            <span className={cx('font-semibold tracking-tight text-body', search?.size === 'compact' && 'hidden xl:inline')}>MessageVault</span>
          </Link>
          <EnvSwitcher />
        </div>
        <div className="pointer-events-auto flex flex-col items-center gap-2.5 min-w-0">
          {search && <SearchField value={search.value} onSubmit={search.onSubmit} size={search.size} />}
          {search?.trail}
        </div>
        <div className="pointer-events-auto flex justify-end items-center gap-3 h-10"><ViewSwitch hero={hero} /><Account /></div>
      </header>
      <main className="relative">{children}</main>
    </div>
  );
}

/** Where the view switch shows: Now, the stage, and the Systems view with its pages. */
export const switchShows = (pathname: string) => pathname === '/' || pathname === '/s' || /^\/(systems|integrations)(\/|$)/.test(pathname);

/**
 * Now · Systems: the two ways to look at the tenant, as a small calm switch beside the avatar. Links (each a history
 * step); on the stage neither is current. Below 1280 px only the icons, labelled for screen readers and on hover.
 */
function ViewSwitch({ hero }: { hero: boolean }) {
  const { pathname } = useLocation();
  const { link } = useEnv();
  if (!switchShows(pathname)) return null;
  const systems = pathname !== '/' && pathname !== '/s';
  return (
    <nav aria-label="View" className={cx('mv-switch', hero && 'hero')}>
      <Link to={link('/')} aria-current={pathname === '/' ? 'page' : undefined} title="Now" aria-label="Now"><PulseIcon size={15} /><span className="mv-switch-label">Now</span></Link>
      <Link to={link('/systems')} aria-current={systems ? 'page' : undefined} title="Systems" aria-label="Systems"><NetworkIcon size={15} /><span className="mv-switch-label">Systems</span></Link>
    </nav>
  );
}

/** A quiet 3 px band at the very top for every environment but the default; a protected one also names itself. */
function EnvBand() {
  const { current } = useEnv();
  if (!current || !showBand(current)) return null;
  return (
    <div aria-hidden={!current.protected} className="fixed inset-x-0 top-0 z-40 h-[3px] pointer-events-none" style={{ background: envColor(current.color) }}>
      {current.protected && (
        <span className="absolute left-6 top-full rounded-b-chip px-2 py-px text-micro font-semibold uppercase tracking-[.08em] text-n1 whitespace-nowrap max-w-[40vw] truncate"
          style={{ background: envColor(current.color) }}>
          {current.name}
        </span>
      )}
    </div>
  );
}

/** The environment in use, as a small pill next to the logo; a menu lists the others with how many vaults are online. */
function EnvSwitcher() {
  const { envs, current, visible, select, link } = useEnv();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Settings pages list every environment themselves (vaults, keys, environments) or are tenant-wide;
  // the data model page has its own, more visible selector in the page.
  const inSettings = pathname.startsWith('/settings');
  if (!visible || !current || inSettings) return null;
  return (
    <Menu.Root>
      <Menu.Trigger aria-label={`Environment: ${current.name}`}
        className="inline-flex items-center gap-2 h-8 pl-2.5 pr-2 rounded-full bg-n2 text-n10 text-small font-medium shadow-node cursor-pointer border-0 outline-none min-w-0 max-w-[180px] focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-n1">
        <i className="size-2 rounded-full shrink-0" style={{ background: envColor(current.color) }} aria-hidden="true" />
        <span className="truncate">{current.name}</span>
        <ChevronIcon size={14} className="text-n7 shrink-0" />
      </Menu.Trigger>
      <Menu.Content align="start" className="min-w-64">
        <Menu.Label>Environment</Menu.Label>
        {envs.map((e) => (
          <Menu.Item key={e.id} onSelect={() => { if (e.id !== current.id) select(e.id); }} aria-current={e.id === current.id ? 'true' : undefined}>
            <span className="flex items-center gap-2.5 w-full min-w-0">
              <i className="size-2 rounded-full shrink-0" style={{ background: envColor(e.color) }} aria-hidden="true" />
              <span className="truncate flex-1">{e.name}</span>
              <span className="text-micro text-n7 whitespace-nowrap">{e.onlineCount} online</span>
              <CheckIcon size={14} className={cx('shrink-0 text-accent', e.id !== current.id && 'invisible')} />
            </span>
          </Menu.Item>
        ))}
        <Menu.Separator />
        <Menu.Item icon={<GearIcon size={16} />} onSelect={() => navigate(link('/settings/environments'))}>Manage environments</Menu.Item>
      </Menu.Content>
    </Menu.Root>
  );
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
}

const THEMES: [ThemePref, string][] = [['mode', 'By mode'], ['light', 'Light'], ['dark', 'Dark']];

/** The avatar and everything behind it: tenant, theme, settings, password, log out. */
function Account() {
  const { me, tenant, setTenantId, logout } = useAuth();
  const { pref, theme, setPref } = useThemePref();
  const navigate = useNavigate();
  const { link } = useEnv();
  const [password, setPassword] = useState(false);
  // The password panel opens once the menu has finished closing, so the two do not fight over focus
  const openPassword = useRef(false);
  const tenants = me?.tenants ?? [];

  return (
    <Popover.Root open={password} onOpenChange={setPassword}>
      <Popover.Anchor asChild>
        <div className="flex items-center">
          <Menu.Root>
            <Menu.Trigger aria-label="Account"
              className="size-8 rounded-full bg-n10 text-n1 text-micro font-semibold tracking-wide grid place-items-center cursor-pointer border-0 outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-n1">
              {initials(me?.user.name ?? '')}
            </Menu.Trigger>
            <Menu.Content align="end" onCloseAutoFocus={(e) => {
              if (openPassword.current) {
                e.preventDefault();
                openPassword.current = false;
                setPassword(true);
              }
            }}>
              <Menu.Label>{me?.user.email}</Menu.Label>
              {tenants.length > 1 && (
                <Menu.Sub>
                  <Menu.SubTrigger icon={<BuildingIcon size={16} />}>{tenant?.name}</Menu.SubTrigger>
                  <Menu.SubContent>
                    {tenants.map((t) => <Menu.Item key={t.id} selected={t.id === tenant?.id} onSelect={() => setTenantId(t.id)}>{t.name}</Menu.Item>)}
                  </Menu.SubContent>
                </Menu.Sub>
              )}
              <Menu.Sub>
                <Menu.SubTrigger icon={theme === 'dark' ? <MoonIcon size={16} /> : <SunIcon size={16} />}>Theme</Menu.SubTrigger>
                <Menu.SubContent>
                  {THEMES.map(([p, label]) => <Menu.Item key={p} selected={pref === p} onSelect={() => setPref(p)}>{label}</Menu.Item>)}
                </Menu.SubContent>
              </Menu.Sub>
              <Menu.Item icon={<GearIcon size={16} />} onSelect={() => navigate(link('/settings'))}>Settings</Menu.Item>
              <Menu.Item icon={<KeyIcon size={16} />} onSelect={() => { openPassword.current = true; }}>Change password</Menu.Item>
              <Menu.Separator />
              <Menu.Item tone="fail" icon={<LogoutIcon size={16} />} onSelect={() => void logout()}>Log out</Menu.Item>
            </Menu.Content>
          </Menu.Root>
        </div>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={8} collisionPadding={12} className="z-50 outline-none mv-pop">
          <ChangePassword onDone={() => setPassword(false)} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

const inputClass = 'h-9 w-full rounded-chip bg-n3 px-3 text-small text-n10 border-0 outline-none focus-visible:ring-[1.5px] focus-visible:ring-accent placeholder:text-n7';

/** Changing the password, in a small panel under the avatar (no dialog). Other sessions are logged out by the cloud. */
function ChangePassword({ onDone }: { onDone: () => void }) {
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await Api.changePassword(current, next);
      toast('Password changed. Other sessions were logged out.');
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Surface level={2} className="w-[300px] p-4">
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-3">
        <div className="text-body font-semibold">Change password</div>
        <label className="flex flex-col gap-1 text-small">
          <span className="text-n8">Current password</span>
          <input type="password" className={inputClass} value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" autoFocus />
        </label>
        <label className="flex flex-col gap-1 text-small">
          <span className="text-n8">New password</span>
          <input type="password" className={inputClass} value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
          <span className={cx('text-micro', error ? 'text-fail' : 'text-n7')}>{error ?? 'At least 10 characters'}</span>
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onDone} className="h-9 px-3 rounded-chip bg-transparent text-n8 text-small font-medium border-0 cursor-pointer hover:bg-n3 hover:text-n10">Cancel</button>
          <button type="submit" disabled={busy || !current || next.length < 10}
            className="h-9 px-4 rounded-chip bg-n10 text-n1 text-small font-semibold border-0 cursor-pointer disabled:opacity-40 disabled:cursor-default">
            Change
          </button>
        </div>
      </form>
    </Surface>
  );
}
